// src/utils/format.ts — Estandarización de formato numérico y monetario en POW
// Formato requerido: separador de miles con coma (,) y decimal con punto (.)

/**
 * Formatea un valor como importe monetario con coma de miles y 2 decimales (o los especificados).
 * Ejemplos:
 *  1234.5  -> "$1,234.50"
 *  0       -> "$0.00"
 *  null    -> "—" o "$0.00" según fallback
 */
export function formatMoney(
  val: number | string | null | undefined,
  options?: {
    prefix?: string
    decimals?: number
    fallback?: string
  }
): string {
  const { prefix = '$', decimals = 2, fallback = '—' } = options || {}

  if (val === null || val === undefined || val === '') {
    return fallback
  }

  const num = typeof val === 'number' ? val : Number(val)
  if (isNaN(num)) {
    return fallback
  }

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  return `${prefix}${formatted}`
}

/**
 * Formatea un número o contador entero/decimal con separador de miles y opcionalmente decimales fijos.
 * Ejemplos:
 *  1500     -> "1,500"
 *  1500000  -> "1,500,000"
 *  null     -> "0" o fallback
 */
export function formatNumber(
  val: number | string | null | undefined,
  options?: {
    decimals?: number
    fallback?: string
  }
): string {
  const { decimals, fallback = '0' } = options || {}

  if (val === null || val === undefined || val === '') {
    return fallback
  }

  const num = typeof val === 'number' ? val : Number(val)
  if (isNaN(num)) {
    return fallback
  }

  if (decimals !== undefined) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  }

  return num.toLocaleString('en-US')
}

/**
 * Formatea un tipo de cambio asegurando 4 decimales con coma de miles y punto decimal.
 * Ejemplo:
 *  3.75   -> "S/ 3.7500"
 *  1234.5 -> "S/ 1,234.5000"
 */
export function formatTipoCambio(
  val: number | string | null | undefined,
  options?: {
    prefix?: string
    fallback?: string
  }
): string {
  const { prefix = 'S/ ', fallback = '—' } = options || {}

  if (val === null || val === undefined || val === '') {
    return fallback
  }

  const num = typeof val === 'number' ? val : Number(val)
  if (isNaN(num)) {
    return fallback
  }

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })

  return `${prefix}${formatted}`
}
